import { Menu, PanelLeft } from 'lucide-react';
import { AvatarMenu } from '@/components/AvatarMenu';
import { GlobalSearch } from '@/components/GlobalSearch';
import { SURFACES } from '@/lib/surfaces';
import { NotificationBell } from '@/components/NotificationBell';
import { ThemeToggle } from '@/components/ThemeToggle';
import { Button } from '@/components/ui/button';

/**
 * The content header. A UTILITY bar, not navigation — navigation is the sidebar, and a
 * second place to navigate from is how a product ends up with two nav systems that
 * disagree about where you are.
 *
 * ⚠️ `[&_svg]:size-5` IS REQUIRED, not decoration. `buttonVariants` ends with
 * `[&_svg]:size-4`, a descendant selector at specificity (0,1,1); a bare `size-5` on the
 * icon is (0,1,0) and LOSES, so the 20px top-bar chrome would silently render at 16px.
 * tailwind-merge treats `[&_svg]:size-*` as one group, so the override on the Button's own
 * class list wins.
 */
export type UtilityBarProps = {
  collapsed: boolean;
  onToggleCollapse: () => void;
  onOpenDrawer: () => void;
};

export const UtilityBar = ({ collapsed, onToggleCollapse, onOpenDrawer }: UtilityBarProps) => (
  <header className="flex h-14 shrink-0 items-center gap-2 border-b border-border px-3">
    <Button
      variant="ghost"
      size="icon"
      className="[&_svg]:size-5 md:hidden"
      onClick={onOpenDrawer}
      aria-label="Open navigation"
    >
      <Menu aria-hidden />
    </Button>

    <Button
      variant="ghost"
      size="icon"
      className="hidden [&_svg]:size-5 md:inline-flex"
      onClick={onToggleCollapse}
      aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
    >
      <PanelLeft aria-hidden />
    </Button>

    {/* ⚠️ [C3c §13a] GATED AT THE MOUNT, so the ⌘K listener goes with the button. See
        `surfaces.ts` — the handler lives inside `GlobalSearch` and there is no central keymap,
        so an unmounted component is the only way to be sure the shortcut is gone too. */}
    {SURFACES.globalSearch ? <GlobalSearch /> : null}

    {/*
     * ⚠️ [amended C1g §1d] THIS SAID "there is no second real utility yet, and a notification
     * bell that notifies nothing is the same defect as a ⌘K button that opens nothing."
     *
     * It notifies something now — outstanding work, DERIVED on every read (§1a), so the count
     * clears when somebody names the last officer and at no other moment. The rule that
     * comment was stating still stands and is now satisfied rather than avoided.
     *
     * ⚠️ IT RENDERS `null` FOR A ROLE WITHOUT `arrangement.read`, and it is the component that
     * decides that rather than this file — the same predicate the route is gated on, so a bell
     * cannot appear for somebody every request behind it would refuse.
     */}
    <div className="ml-auto flex items-center gap-2">
      <NotificationBell />
      <ThemeToggle />
      <AvatarMenu />
    </div>
  </header>
);
